import { Component } from '@theme/component';
import { fetchConfig, formatMoneyWithTemplate } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Bundles the current product with its curated "Complete the Collection" pairing
 * into a single /cart/add.js request, then dispatches the theme's real cart
 * events so the cart drawer opens and re-renders like any other add-to-cart.
 * @extends {Component}
 */
class CompleteTheCollectionBundle extends Component {
  /**
   * Handles a paired-item checkbox being toggled: keeps at least one item selected,
   * then refreshes the bundle price/heading/subtext to match the current selection.
   * @param {Event} event
   */
  updateSelection(event) {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;

    const checkboxes = this.#checkboxes();
    const checkedCount = checkboxes.filter((box) => box.checked).length;

    if (checkedCount === 0) {
      checkbox.checked = true;
      return;
    }

    this.#updateBundleSummary(checkboxes);
  }

  addAll() {
    const button = this.refs.addButton;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const items = this.#collectItems();
    if (items.length === 0) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.labelAdding || originalText;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
      .filter(Boolean);

    fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json'),
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id || 'complete-the-collection', response.message, response.description, response.errors)
          );
          button.textContent = this.dataset.labelError || originalText;
          return;
        }

        button.textContent = this.dataset.labelAdded || originalText;

        this.dispatchEvent(
          new CartAddEvent({}, this.id || 'complete-the-collection', {
            source: 'complete-the-collection',
            itemCount: items.length,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        button.textContent = this.dataset.labelError || originalText;
      })
      .finally(() => {
        setTimeout(() => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = originalText;
        }, 2000);
      });
  }

  /**
   * @returns {Array<{ id: number, quantity: number }>}
   */
  #collectItems() {
    /** @type {Array<{ id: number, quantity: number }>} */
    const items = [];

    const formId = this.dataset.buyButtonsFormId;
    const variantInput = formId ? document.getElementById(formId)?.querySelector('[name="id"]') : null;
    const currentVariantId = variantInput instanceof HTMLInputElement ? Number(variantInput.value) : null;

    if (currentVariantId) items.push({ id: currentVariantId, quantity: 1 });

    for (const card of this.querySelectorAll('[data-paired-variant-id]')) {
      if (card.getAttribute('data-paired-available') !== 'true') continue;

      const checkbox = card.querySelector('.complete-collection__checkbox');
      if (checkbox instanceof HTMLInputElement && !checkbox.checked) continue;

      const id = Number(card.getAttribute('data-paired-variant-id'));
      if (id) items.push({ id, quantity: 1 });
    }

    return items;
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  #checkboxes() {
    const refs = this.refs.itemCheckboxes;
    const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
    return list.filter((ref) => ref instanceof HTMLInputElement);
  }

  /**
   * Recomputes the bundle price, heading count, and item list from the checked items.
   * No-ops when the bundle bar isn't rendered (fewer than 2 paired products).
   * @param {HTMLInputElement[]} checkboxes
   */
  #updateBundleSummary(checkboxes) {
    const { bundlePrice, bundleHeading, bundleSub, moneyFormat } = this.refs;
    if (!(bundlePrice instanceof HTMLElement)) return;

    const checkedBoxes = checkboxes.filter((box) => box.checked);
    const basePrice = Number(this.dataset.productPrice) || 0;
    const titles = [];
    let total = basePrice;

    for (const box of checkedBoxes) {
      const item = box.closest('.complete-collection__item');
      if (!(item instanceof HTMLElement)) continue;
      total += Number(item.dataset.pairedPrice) || 0;
      if (item.dataset.pairedTitle) titles.push(item.dataset.pairedTitle);
    }

    const template = moneyFormat instanceof HTMLTemplateElement ? moneyFormat.content.textContent ?? '' : '';
    bundlePrice.textContent = formatMoneyWithTemplate(total, template, this.dataset.currency);

    if (bundleHeading instanceof HTMLElement && this.dataset.bundleHeadingTemplate) {
      bundleHeading.textContent = this.dataset.bundleHeadingTemplate.replace(
        '[count]',
        String(checkedBoxes.length + 1)
      );
    }

    if (bundleSub instanceof HTMLElement) {
      bundleSub.textContent = [this.dataset.productTitle, ...titles].filter(Boolean).join(' + ');
    }
  }
}

if (!customElements.get('complete-the-collection-bundle')) {
  customElements.define('complete-the-collection-bundle', CompleteTheCollectionBundle);
}
